/**
 * Required Query Params Guard
 * 
 * Checks for required query parameters and shows a helpful error message
 * if they're missing. This prevents silent failures and confusing behavior.
 * 
 * Required params:
 * - club-id: The club ID
 * - tenant-handle: The tenant handle
 * 
 * **Development/Staging Only**: In production, shows a simple error message
 * since end users have no control over URL parameters.
 * 
 * @module lib/guards
 */

import { useEffect, useRef } from 'react';
import { captureMessage } from '@butter/monitoring/web';
import { useClubId, useTenantHandle } from '~/services/appRuntime';

interface MissingParamsGuardProps {
  children: React.ReactNode;
}

const IS_PRODUCTION = import.meta.env.PROD && import.meta.env.MODE === 'production';

/**
 * Guard component that checks for required query parameters
 * Shows detailed error in dev/staging, simple error in production
 */
export function MissingParamsGuard({ children }: MissingParamsGuardProps) {
  const [clubId] = useClubId();
  const [tenantHandle] = useTenantHandle();

  // appRuntime reads URL params synchronously before React renders, so the
  // hooks return the correct values on the first render. We can decide
  // immediately — no timeout, no flash of children before the error shows.
  const showError = !clubId || !tenantHandle;

  // Report to Sentry once per mount when a user lands without the params
  // required to use the portal at all. The ref guard prevents duplicate events
  // from StrictMode's double-invoke or any param change while showError stays true.
  const reported = useRef(false);
  useEffect(() => {
    if (showError && !reported.current) {
      reported.current = true;
      console.error('[MissingParamsGuard] Missing required query parameters:', {
        clubId,
        tenantHandle,
        currentUrl: window.location.href,
        environment: import.meta.env.MODE,
      });
      captureMessage('Web portal accessed with missing required params', 'warning', {
        extra: {
          missingParams: [...(!clubId ? ['club-id'] : []), ...(!tenantHandle ? ['tenant-handle'] : [])],
          url: window.location.href,
          referrer: document.referrer || null,
        },
      });
    }
  }, [showError, clubId, tenantHandle]);

  // If params are missing, show error
  if (showError) {
    // Production: Simple, user-friendly error (no technical details)
    if (IS_PRODUCTION) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg 
                className="w-8 h-8 text-red-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Something Went Wrong
            </h1>
            <p className="text-gray-600 mb-6">
              Check your link or contact the club for assistance.
            </p>
            
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    // Development/Staging: Detailed error for developers
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 border-2 border-red-200">
          <div className="flex items-start gap-4">
            {/* Error Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg 
                  className="w-6 h-6 text-red-600" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                  />
                </svg>
              </div>
            </div>

            {/* Error Content */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Missing Configuration
              </h1>
              <p className="text-gray-700 mb-4">
                This page requires additional information to load properly.
              </p>

              {/* What's Missing */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">
                  Missing Parameters:
                </h2>
                <ul className="space-y-1">
                  {!clubId && (
                    <li className="flex items-center gap-2 text-sm text-red-600">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                      <code className="bg-red-50 px-2 py-0.5 rounded">club-id</code>
                    </li>
                  )}
                  {!tenantHandle && (
                    <li className="flex items-center gap-2 text-sm text-red-600">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                      <code className="bg-red-50 px-2 py-0.5 rounded">tenant-handle</code>
                    </li>
                  )}
                </ul>
              </div>

              {/* Example URL */}
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">
                  Example URL:
                </h2>
                <code className="text-xs text-gray-600 break-all block bg-white p-2 rounded border border-blue-200">
                  {window.location.origin}?club-id=2&tenant-handle=drivingrangecollective
                </code>
              </div>

              {/* Current URL */}
              <details className="text-sm text-gray-600">
                <summary className="cursor-pointer font-medium hover:text-gray-900">
                  Current URL
                </summary>
                <code className="block mt-2 text-xs bg-gray-100 p-2 rounded break-all">
                  {window.location.href}
                </code>
              </details>

              {/* Help Text */}
              <p className="text-sm text-gray-600 mt-4">
                If you're a developer, make sure the URL includes the required query parameters. 
                If you're a user, please contact support for a valid booking link.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Params are present, render children
  return <>{children}</>;
}
